import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import API_BASE_URL from "./config";
import "./FrameOrderStatus.css";

const STEPS = [
  { key: "paid", label: "Paid", icon: "💳" },
  { key: "shipped", label: "Shipped", icon: "📦" },
  { key: "delivered", label: "Delivered", icon: "🏡" },
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function FrameOrderStatus() {
  const { trackingToken } = useParams();
  const [searchParams] = useSearchParams();
  const paymentResult = searchParams.get("payment");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/frame-orders/${trackingToken}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Order not found");
        return;
      }
      setOrder(data);
    } catch {
      setError("Failed to load order status");
    } finally {
      setLoading(false);
    }
  }, [trackingToken]);

  useEffect(() => {
    load();
  }, [load]);

  const currentStepIndex = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="fos-container">
      <div className="fos-card">
        {loading ? (
          <p className="fos-loading">Loading...</p>
        ) : error ? (
          <>
            <div className="fos-icon">❌</div>
            <h1 className="fos-title">{error}</h1>
          </>
        ) : order.status === "awaiting_payment" ? (
          <>
            <div className="fos-icon">{paymentResult === "failed" ? "❌" : "⏳"}</div>
            <h1 className="fos-title">{paymentResult === "failed" ? "Payment failed" : "Payment pending"}</h1>
            <p className="fos-subtitle">Order #{order.order_number} — {order.frame_label} frame, ₹{parseFloat(order.total_amount).toFixed(0)}</p>
            <p className="fos-note">This order hasn't been paid for yet. Please go back and try again.</p>
          </>
        ) : (
          <>
            <div className="fos-icon">🖼️</div>
            <h1 className="fos-title">Order #{order.order_number}</h1>
            <p className="fos-subtitle">
              {order.frame_label} frame, ₹{parseFloat(order.total_amount).toFixed(0)} — {order.shipping_city}, {order.shipping_state}
            </p>

            <div className="fos-stepper">
              {STEPS.map((step, i) => {
                const reached = i <= currentStepIndex;
                const dateField = step.key === "paid" ? order.created_at : step.key === "shipped" ? order.shipped_at : order.delivered_at;
                return (
                  <div className={`fos-step ${reached ? "fos-step--reached" : ""}`} key={step.key}>
                    <div className="fos-step-icon">{step.icon}</div>
                    <div className="fos-step-label">{step.label}</div>
                    {reached && dateField && <div className="fos-step-date">{formatDate(dateField)}</div>}
                    {i < STEPS.length - 1 && <div className={`fos-step-line ${i < currentStepIndex ? "fos-step-line--reached" : ""}`} />}
                  </div>
                );
              })}
            </div>

            {order.status === "shipped" || order.status === "delivered" ? (
              <p className="fos-note">Shipped via {order.carrier_name} — tracking #{order.tracking_number}</p>
            ) : (
              <p className="fos-note">Your studio will print, frame, and ship this photo soon.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FrameOrderStatus;
